'use client';

import { StatusBadgeChip, type StatusBadgeColors } from '@/components/common';
import { ItemTrackingStatus } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { LinearProgress } from '@andritzot/metris-web-ui/feedback/progress/linear-progress';
import { Grid } from '@andritzot/metris-web-ui/layout/grid';
import { useMemo } from 'react';

const getStatusColor = (statusOrder: number, isRelease: boolean): StatusBadgeColors => {
  if (isRelease) return 'green';
  switch (statusOrder) {
    case 1:
      return 'grey';
    case 2:
      return 'lightyellow';
    case 3:
      return 'lightBlue';
    case 4:
      return 'blue';
    case 5:
      return 'lightGreen';
    case 6:
      return 'green';
    default:
      return 'green';
  }
};

export function StatusField() {
  const { status, type } = useRoadmapEditStore((s) => s.editableRoadMap);

  const percentComplete = useMemo(() => {
    const allItems = type?.roadmapStatus?.flatMap((s) => s.roadmapStatusItems) ?? [];
    if (allItems.length === 0) return null;
    const completed = allItems.filter(
      (item) => item.status === ItemTrackingStatus.Completed || item.status === ItemTrackingStatus.NotApplicable,
    ).length;
    return Math.round((completed / allItems.length) * 100);
  }, [type?.roadmapStatus]);

  if (!status) return null;
  return (
    <Grid container size={12} alignItems="center" wrap="nowrap" sx={{ gap: 1 }}>
      <Grid size={2}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Item Status
        </Typography>
      </Grid>
      <Grid size={2}>
        <Tooltip title={`${percentComplete ?? 0}% of planning items completed`} placement="right">
          <LinearProgress variant="determinate" value={percentComplete ?? 0} />
        </Tooltip>
      </Grid>
      <Grid size={8}>
        <Tooltip title="Status is automatically set according to the planning status" placement="right">
          <StatusBadgeChip color={getStatusColor(status.statusOrder, status.isRelease)} sx={{ width: '100%' }}>
            {status.name} 
          </StatusBadgeChip>
        </Tooltip>
      </Grid>
    </Grid>
  );
}
